/**
 * In-memory registry of parsed fonts (SHX + TTF/OTF) used by the
 * renderer and UI.
 *
 * The registry is a **singleton** (module-level `Map`) because the
 * renderer must look up a font by style name on every TEXT / MTEXT
 * draw and cannot afford React context propagation. Mutations are
 * broadcast through a `useSyncExternalStore`-compatible subscribe
 * mechanism so components like `FontManagerDialog` and
 * `TextStyleEditorDialog` re-render when a font is added / removed.
 *
 * ### Unified SHX + TTF
 *
 * Per the Option-2 roadmap (see `plans/indexed-painting-perlis.md`
 * §"Revision A"), both SHX and TTF fonts live in the same map as a
 * discriminated `RegisteredFont` union. Callers that specifically need
 * SHX vector glyphs (the built-in stroke renderer) use `getShx()`;
 * callers that only care about whether the browser can lay the family
 * out (the SVG/canvas `<text>` fallback) use `getTtf()`. Code that is
 * kind-agnostic (the missing-fonts tracker, the font manager dialog)
 * works off the union directly.
 *
 * ### Keys
 *
 * All keys are normalised via `normalizeFontKey` from `fontStore.ts`:
 *   - trimmed
 *   - lower-cased
 *   - `.shx` suffix stripped
 *
 * so `"ROMANS.SHX"`, `" romans "`, and `"romans"` all hit the same
 * entry. This matches AutoCAD behaviour where style `fontName`
 * matching is case-insensitive and suffix-agnostic. TTF families
 * without a `.shx` suffix are unaffected by the stripping rule.
 *
 * ### Why not Zustand?
 *
 * The registry exposes non-React consumers (`FontRegistry.get` is
 * called from `renderSimpleEntities.tsx` during SVG generation, which
 * runs in a tight loop and needs a sync read). A thin manual subscribe
 * avoids pulling zustand into the hot path and keeps the API exactly
 * as simple as `useSyncExternalStore` expects.
 */

import {
  parseShxFont,
  type ShxFont,
} from "@dxf-ai-editor/shx-parser";
import { useSyncExternalStore } from "react";

import { normalizeFontKey } from "./fontStore";
import {
  detectFontKind,
  loadTtfFromBytes,
  unloadTtf,
  type LoadedTtf,
} from "./ttfFontLoader";

/**
 * A single registry entry. Discriminated by `kind` so both the raw
 * bytes (SHX glyph table is derived from them via the parser) and the
 * browser-level FontFace (for TTF) are addressable through one map.
 *
 * The `bytes` are always kept on SHX entries so the drawing can
 * re-export its embedded fonts; TTF entries keep the bytes on the
 * nested `ttf` object (see `LoadedTtf`) for the same reason.
 */
export type RegisteredFont =
  | {
      kind: "shx";
      /** Canonical display name from the SHX header. */
      name: string;
      font: ShxFont;
      /** Raw bytes preserved for drawing re-export. */
      bytes: Uint8Array;
    }
  | {
      kind: "ttf";
      /** CSS font-family token (see `LoadedTtf.family`). */
      name: string;
      ttf: LoadedTtf;
    };

/**
 * Discriminated result of `FontRegistry.register`. The `error.code`
 * strings overlap with those from `ShxParseResult` and
 * `TtfLoadResult` — `FontManagerDialog` renders a translated message
 * per code.
 */
export type RegisterResult =
  | { ok: true; entry: RegisteredFont }
  | {
      ok: false;
      error: {
        code: string;
        message: string;
      };
    };

const fonts = new Map<string, RegisteredFont>();
const subscribers = new Set<() => void>();
let version = 0;

function notify(): void {
  version += 1;
  for (const fn of subscribers) {
    try {
      fn();
    } catch (error) {
      // A buggy subscriber must not block sibling subscribers from
      // being notified — log and keep going. `console.error` is the
      // project-wide channel for developer-facing errors (see other
      // renderer files).
      console.error("[fonts/registry] subscriber threw:", error);
    }
  }
}

/**
 * Public registry surface. `register` is the only async method — it
 * awaits the browser's internal TTF parse inside the `FontFace` API.
 * All other methods stay synchronous so the renderer can read in a
 * tight loop.
 */
export const FontRegistry = {
  /**
   * Detect the format of `bytes` and register the resulting font.
   *
   *   - SHX: parsed synchronously via `parseShxFont`. The caller's
   *     error contains `{code, message}` from the parser.
   *   - TTF/OTF: loaded via `loadTtfFromBytes`. `hintedName` is used
   *     as the CSS family when provided; otherwise a deterministic
   *     synthetic name is derived from the bytes.
   *   - unknown: returns `{ok:false, error:{code:"unknown-format"}}`
   *     so the UI can surface "Unsupported font format" without
   *     leaking binary details.
   */
  async register(
    bytes: Uint8Array,
    hintedName?: string
  ): Promise<RegisterResult> {
    const kind = detectFontKind(bytes);

    if (kind === "shx") {
      const result = parseShxFont(bytes);
      if (!result.ok) {
        return {
          ok: false,
          error: { code: result.error.code, message: result.error.message },
        };
      }
      const entry: RegisteredFont = {
        kind: "shx",
        name: result.font.name,
        font: result.font,
        bytes,
      };
      // Overwriting an existing TTF with an SHX (or vice versa) under
      // the same normalised key evicts the old entry cleanly: if it
      // was a TTF, we must first revoke its FontFace so the browser's
      // document.fonts set doesn't leak.
      evictExistingFontFace(normalizeFontKey(entry.name));
      fonts.set(normalizeFontKey(entry.name), entry);
      notify();
      return { ok: true, entry };
    }

    if (kind === "ttf") {
      const result = await loadTtfFromBytes(bytes, hintedName);
      if (!result.ok) {
        return {
          ok: false,
          error: { code: result.code, message: result.message },
        };
      }
      const entry: RegisteredFont = {
        kind: "ttf",
        name: result.ttf.family,
        ttf: result.ttf,
      };
      evictExistingFontFace(normalizeFontKey(entry.name));
      fonts.set(normalizeFontKey(entry.name), entry);
      notify();
      return { ok: true, entry };
    }

    return {
      ok: false,
      error: {
        code: "unknown-format",
        message: "Not a recognised SHX or TTF/OTF font",
      },
    };
  },

  /**
   * Look up a font by logical name. Accepts any of the forms listed
   * in the module docstring (case-insensitive, suffix optional).
   * Returns the full `RegisteredFont` union — callers that need a
   * specific kind should use `getShx` / `getTtf`.
   */
  get(name: string): RegisteredFont | undefined {
    return fonts.get(normalizeFontKey(name));
  },

  /**
   * Convenience: return the parsed `ShxFont` if the entry under
   * `name` is an SHX font, otherwise `undefined`. Used by the stroke
   * renderer which cannot render TTFs through its vector path.
   */
  getShx(name: string): ShxFont | undefined {
    const entry = this.get(name);
    return entry?.kind === "shx" ? entry.font : undefined;
  },

  /**
   * Convenience: return the loaded `LoadedTtf` if the entry under
   * `name` is a TTF font, otherwise `undefined`. Used by the SVG /
   * canvas text fallback which emits `font-family` and relies on
   * `document.fonts` for glyph resolution.
   */
  getTtf(name: string): LoadedTtf | undefined {
    const entry = this.get(name);
    return entry?.kind === "ttf" ? entry.ttf : undefined;
  },

  /**
   * Existence check. Prefer this over `get(...) !== undefined` — the
   * intent reads better at call sites (e.g. the missing-fonts
   * fallback path).
   */
  has(name: string): boolean {
    return fonts.has(normalizeFontKey(name));
  },

  /**
   * Every registered font, sorted by display name A→Z for stable UI
   * rendering. The sort uses `localeCompare` so Cyrillic / accented
   * font names land in a user-expected order.
   */
  list(): RegisteredFont[] {
    return Array.from(fonts.values()).sort((a, b) =>
      a.name.localeCompare(b.name)
    );
  },

  /**
   * Remove a font. For TTF entries the backing `FontFace` is revoked
   * from `document.fonts` before the map entry is deleted so the
   * browser stops resolving the family. No-op if absent (doesn't
   * notify either, to avoid waking subscribers for a non-event).
   */
  remove(name: string): void {
    const key = normalizeFontKey(name);
    const entry = fonts.get(key);
    if (!entry) return;
    if (entry.kind === "ttf") {
      unloadTtf(entry.ttf);
    }
    fonts.delete(key);
    notify();
  },

  /**
   * Drop every font. TTF faces are revoked from `document.fonts`
   * individually so the browser's in-memory set stays in sync.
   */
  clear(): void {
    if (fonts.size === 0) return;
    for (const entry of fonts.values()) {
      if (entry.kind === "ttf") {
        unloadTtf(entry.ttf);
      }
    }
    fonts.clear();
    notify();
  },

  /**
   * Subscribe to registry mutations. Returns an unsubscribe function
   * suitable for use as a `useEffect` cleanup. The first argument is
   * intentionally the snapshot-returning callback shape expected by
   * React's `useSyncExternalStore`.
   */
  subscribe(listener: () => void): () => void {
    subscribers.add(listener);
    return () => {
      subscribers.delete(listener);
    };
  },

  /**
   * Opaque version token. Increments on every `notify()` call; used
   * as the snapshot for `useSyncExternalStore`. Consumers never
   * interpret the numeric value — they just need an identity that
   * changes when the registry mutates.
   */
  getSnapshot(): number {
    return version;
  },

  /**
   * Number of registered fonts. Exposed for diagnostics / tests;
   * production code should prefer `list().length` when it needs the
   * actual collection.
   */
  size(): number {
    return fonts.size;
  },
};

/**
 * If the given normalised key resolves to a TTF entry, revoke its
 * `FontFace` from `document.fonts`. Used internally to keep the
 * browser's font set in sync when a `register()` overwrites an
 * existing entry under the same key.
 */
function evictExistingFontFace(normalisedKey: string): void {
  const existing = fonts.get(normalisedKey);
  if (existing?.kind === "ttf") {
    unloadTtf(existing.ttf);
  }
}

/**
 * React hook returning the current list of registered fonts, sorted
 * for UI display. Re-renders the host component whenever
 * `FontRegistry` mutates.
 *
 * Implementation notes: we subscribe to the underlying `version`
 * snapshot (a cheap integer) and recompute the sorted array on every
 * render. For the font counts we realistically see (<100 per user)
 * this is a noop cost-wise compared to the rest of the dialog.
 */
export function useFontRegistry(): RegisteredFont[] {
  useSyncExternalStore(
    FontRegistry.subscribe,
    FontRegistry.getSnapshot,
    // Server snapshot — identical to the client one because the
    // registry is purely in-memory. SSR never runs in this app but
    // React strict-mode in dev requires a defined function here.
    FontRegistry.getSnapshot
  );
  return FontRegistry.list();
}

/**
 * React hook returning a stable `has(name)` predicate that re-evaluates
 * when the registry mutates. Used by the `TextStyleEditorDialog`
 * select to mark a missing font with `"(not loaded)"`.
 * @public
 */
export function useFontRegistryHas(name: string | undefined): boolean {
  useSyncExternalStore(
    FontRegistry.subscribe,
    FontRegistry.getSnapshot,
    FontRegistry.getSnapshot
  );
  if (name === undefined || name.trim() === "") return false;
  return FontRegistry.has(name);
}
